import { Utils } from "./utils";
import dateFormat from "dateformat";
import dayjs, { PluginFunc } from 'dayjs';
import * as customParseFormat from 'dayjs/plugin/customParseFormat';
import { Strings } from "./strings";
import { Objects } from "./objects";

export class DatesInternal {

    add(d: Date, type: 'milli' | 'second' | 'minute' | 'hour' | 'day' | 'week' | 'month' | 'year' | 'weekDay', amount: number): Date {
        switch (type) {
            case 'milli':
                return new Date(new Date(d).setMilliseconds(d.getMilliseconds() + amount));
            case 'second':
                return new Date(new Date(d).setSeconds(d.getSeconds() + amount));
            case 'minute':
                return new Date(new Date(d).setMinutes(d.getMinutes() + amount));
            case 'hour':
                return new Date(new Date(d).setHours(d.getHours() + amount));
            case 'day':
                return new Date(new Date(d).setDate(d.getDate() + amount));
            case 'week':
                return new Date(new Date(d).setDate(d.getDate() + amount * 7));
            case 'month':
                return new Date(new Date(d).setMonth(d.getMonth() + amount));
            case 'year':
                return new Date(new Date(d).setFullYear(d.getFullYear() + amount));
            case 'weekDay':
                if (amount === 0) {
                    return new Date(d);
                }

                let result = new Date(d);
                let direction = amount > 0 ? 1 : -1;
                let remaining = Math.abs(amount);

                while (remaining > 0) {

                    result.setDate(result.getDate() + direction);

                    let day = result.getDay(); 

                    if (day !== 0 && day !== 6) {
                        remaining--;
                    }
                }

                return new Date(result);
            default:
                throw new Error("Ungültuger type '" + type + "'");
        }
    }

    calendarWeek(date: Date): number {
        let currentThursday = new Date(date.getTime() + (3 - (date.getDay() + 6) % 7) * 86_400_000);
        let yearOfThursday = currentThursday.getFullYear();
        let firstThursday = new Date(new Date(yearOfThursday, 0, 4).getTime() + (3 - (new Date(yearOfThursday, 0, 4).getDay() + 6) % 7) * 86_400_000);
        return Math.floor(1 + 0.5 + (currentThursday.getTime() - firstThursday.getTime()) / 86_400_000 / 7);
    }

    dayOfWeek(date: Date): number {
        return (date.getDay() + 6) % 7;
    }

    diffDays(pastDate: Date, futureDate: Date): number {
        return (new Date(futureDate.getFullYear(), futureDate.getMonth(), futureDate.getDate()).getTime() - new Date(pastDate.getFullYear(), pastDate.getMonth(), pastDate.getDate()).getTime()) / (1_000 * 60 * 60 * 24);
    }

    diffMillis(pastDate: Date, futureDate: Date): number {
        return Math.floor(futureDate.getTime() - pastDate.getTime());
    }

    diffMinutes(pastDate: Date, futureDate: Date): number {
        return Math.floor((futureDate.getTime() - pastDate.getTime()) / 60_000);
    }

    diffSeconds(pastDate: Date, futureDate: Date): number {
        return Math.floor((futureDate.getTime() - pastDate.getTime()) / 1_000);
    }

    endOf(d: Date, type: "day" | "week" | "month" | "year"): Date {
        return dayjs(d).endOf(type).toDate();
    }

    eq(d1: Date, d2: Date): boolean {
        return d1 == null ? d2 == null : d2 != null && d1.getTime() == d2.getTime();
    }

    format(date: Date, format: string): string {
        try {
            return Utils.isFunction(dateFormat) ? (dateFormat as (date: Date, format: string) => string)(date, format) : (dateFormat as any).default(date, format);
        }
        catch {
            throw new Error("Das Datum " + Objects.toStringWithType(date) + " kann nicht formatiert werden");
        }
    }

    formatDateInternational(date: Date): string {
        return this.format(date, "yyyy-mm-dd");
    }

    formatDefaultDate(date: Date): string {
        return this.format(date, "dd.mm.yyyy");
    }

    formatDefault(date: Date): string {
        return this.format(date, "dd.mm.yyyy HH:MM:ss");
    }

    formatNoSeconds(date: Date): string {
        return this.format(date, "dd.mm.yyyy HH:MM");
    }

    fromString(s: string, format: string, throwIfInvalid = true): Date {
        dayjs.extend((customParseFormat as any).default as PluginFunc);
        let result = dayjs(s, format).toDate();
        if (!this.isValid(result)) {
            if (throwIfInvalid) {
                throw new Error("Das Datum '" + s + "' kann nicht mit dem Format '" + format + "' geparsed werden");
            }
            else {
                return null;
            }
        }
        return result;
    }

    fromTimeString(s: string): number {
        let parts = Strings.splitOnce(s, ":");
        return parts.length != 2 || !Utils.isNumberString(parts[0]) || !Utils.isNumberString(parts[1]) ? null : Utils.toNumber(parts[0]) * 60 + Utils.toNumber(parts[1]);
    }

    geq(d1: Date, d2: Date): boolean {
        return d1.getTime() >= d2.getTime();
    }

    getMonthName(monthIndex: number): string {
        return ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"][monthIndex];
    }

    getMonthNameShort(monthIndex: number): string {
        return ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"][monthIndex];
    }

    greater(d1: Date, d2: Date): boolean {
        return d1.getTime() > d2.getTime();
    }

    is(object: any): boolean {
        return object instanceof Date;
    }

    isSameDay(d1: Date, d2: Date): boolean {
        return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();
    }

    isValid(d: Date): boolean {
        return d == null || !Number.isNaN(d.getTime());
    }

    isWeekday(d: Date): boolean {
        let day = d.getDay();
        return day != 0 && day != 6;
    }

    less(d1: Date, d2: Date): boolean {
        return d1.getTime() < d2.getTime();
    }

    leq(d1: Date, d2: Date): boolean {
        return d1.getTime() <= d2.getTime();
    }

    startOf(d: Date, type: "day" | "week" | "month" | "year"): Date {
        //Montag erster Tag der Woche
        dayjs.Ls.en.weekStart = 1;
        return dayjs(d).startOf(type).toDate();
    }

    startOfNext(d: Date, type: "day" | "week" | "month" | "year"): Date {
        //Montag erster Tag der Woche
        dayjs.Ls.en.weekStart = 1;
        return dayjs(d).endOf(type).add(1, "millisecond").toDate();
    }

    to(obj: any): Date {
        if (obj instanceof Date) {
            return obj;
        }
        else if (Strings.is(obj)) {
            return Strings.regexMatches(obj as string, "[0-9]{2}\\.[0-9]{2}\\.[0-9]{4}") ? Dates.fromString(obj as string, "DD.MM.YYYY") : new Date(obj as string);
        }
        else if (Utils.isNumber(obj)) {
            return new Date(obj as number);
        }
        else {
            throw new TypeError("Konnte '" + obj + "' nicht in Date konvertieren");
        }
    }

    toLocalIsoDateString(date: Date): string {
        let year = date.getFullYear();
        let month = String(date.getMonth() + 1).padStart(2, "0");
        let day = String(date.getDate()).padStart(2, "0");

        return year + "-" + month + "-" + day;
    }

    toTimeString(minutes: number): string {
        return minutes == null ? "" : Objects.toString(minutes / 60).padStart(2, "0") + ":" + Objects.toString(minutes % 60).padStart(2, "0");
    }

    toUtc(date: Date): Date {
        return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds()));
    }

    withoutTime(d: Date): Date {
        return new Date(d.getFullYear(), d.getMonth(), d.getDate());
    }

}

export let Dates = new DatesInternal();